import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import { Server } from '../types/models/Server';
import { ApiResponseServerShow } from '../types/api/ApiResponseServer';

interface UseServerReturn {
  server: Server | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useServer = (serverId: string | number): UseServerReturn => {
  const [server, setServer] = useState<Server | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchServer = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/datacenters/servers/${serverId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseServerShow;
      if (data.status === 'success' && data.data?.server) {
        setServer(data.data.server);
      } else {
        throw new Error('Server not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load server'));
    } finally {
      setLoading(false);
    }
  }, [serverId, apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchServer();
    }
  }, [auth.isAuthenticated, fetchServer]);

  return { server, loading, error, refetch: fetchServer };
};
